import type {
  CancellationRequest,
  CounterSale,
  OpticalSale,
  WorkDetail,
  WorkPaymentHistoryItem,
} from "@cosmo/contracts";

const RECEIPT_WIDTH = 226.77;
const MARGIN = 11;
const CONTENT_WIDTH = RECEIPT_WIDTH - MARGIN * 2;
const NAVY = { r: 0.02, g: 0.16, b: 0.27 };
const TEAL = { r: 0.02, g: 0.54, b: 0.56 };
const MUTED = { r: 0.31, g: 0.43, b: 0.49 };
const WHITE = { r: 1, g: 1, b: 1 };
const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

type Align = "left" | "center" | "right";
export type TicketBlock =
  | { kind: "text"; text: string; bold?: boolean; size?: number; align?: Align; muted?: boolean }
  | { kind: "pair"; label: string; value: string; bold?: boolean }
  | { kind: "charge"; label: string; details?: string; value: string }
  | { kind: "rule" }
  | { kind: "section"; text: string }
  | { kind: "total"; label: string; value: string }
  | { kind: "spacer"; height: number }
  | { kind: "signatures"; left: string; right: string };

export type TicketPageModel = {
  title: string;
  folio: string;
  subtitle?: string;
  attendant?: string;
  blocks: TicketBlock[];
  footer?: string;
};

export type OpticalDocumentFilenameKind =
  | "FULL_PACKAGE"
  | "CUSTOMER_TICKET"
  | "OPTICAL_COPY"
  | "CUSTOMER_FRAME_RESPONSIVA"
  | "LABORATORY_ORDER";

type Font = { widthOfTextAtSize: (text: string, size: number) => number };

function safeText(value: string): string {
  return value
    .replace(/[·•]/g, "-")
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\u00a0/g, " ");
}

function safeFolio(value: string): string {
  return value.trim().replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-").replace(/\s+/g, "-");
}

export function opticalDocumentFilename(folio: string, kind: OpticalDocumentFilenameKind): string {
  const suffix: Record<OpticalDocumentFilenameKind, string> = {
    FULL_PACKAGE: "",
    CUSTOMER_TICKET: "-Ticket-Cliente",
    OPTICAL_COPY: "-Copia-Optica",
    CUSTOMER_FRAME_RESPONSIVA: "-Responsiva-Armazon-Cliente",
    LABORATORY_ORDER: "-Orden-Laboratorio",
  };
  return `${safeFolio(folio)}${suffix[kind]}.pdf`;
}

function dateOnly(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function wrap(font: Font, source: string, size: number, width = CONTENT_WIDTH): string[] {
  const words = safeText(source).split(/\s+/).filter(Boolean);
  const rows: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && font.widthOfTextAtSize(candidate, size) > width) {
      rows.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) rows.push(current);
  return rows.length ? rows : [""];
}

let logoPromise: Promise<Uint8Array | null> | null = null;
async function loadLogo(): Promise<Uint8Array | null> {
  if (!logoPromise) {
    logoPromise = fetch("/logo-cosmo-opticas.jpg")
      .then(async (response) => response.ok ? new Uint8Array(await response.arrayBuffer()) : null)
      .catch(() => null);
  }
  return logoPromise;
}

function blockHeight(block: TicketBlock, regular: Font, bold: Font): number {
  if (block.kind === "spacer") return block.height;
  if (block.kind === "rule") return 11;
  if (block.kind === "section") return 34;
  if (block.kind === "total") return 29;
  if (block.kind === "signatures") return 48;
  if (block.kind === "charge") {
    const detailRows = block.details ? wrap(regular, block.details, 6.7).length : 0;
    return 17 + detailRows * 8;
  }
  if (block.kind === "pair") {
    const labelWidth = regular.widthOfTextAtSize(safeText(block.label), 7.4);
    const valueFont = block.bold ? bold : regular;
    const valueWidth = valueFont.widthOfTextAtSize(safeText(block.value), 7.4);
    return labelWidth + valueWidth <= CONTENT_WIDTH - 3
      ? 15
      : 13 + wrap(valueFont, block.value, 7.4).length * 9;
  }
  const size = block.size || 8;
  return wrap(block.bold ? bold : regular, block.text, size).length * (size + 3) + 2;
}

function drawCentered(
  page: { drawText: (text: string, options: Record<string, unknown>) => void },
  font: Font,
  text: string,
  y: number,
  size: number,
  color: unknown,
): void {
  const clean = safeText(text);
  const width = font.widthOfTextAtSize(clean, size);
  page.drawText(clean, { x: (RECEIPT_WIDTH - width) / 2, y, size, font, color });
}

async function renderTicketPages(models: TicketPageModel[], suppliedLogo?: Uint8Array | null): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  document.setTitle(models[0]?.folio || "COSMO-OPTICAS");
  document.setAuthor("Cosmo Ópticas");
  document.setSubject("Documentos de venta");
  document.setCreator("Cosmo Ópticas");
  document.setProducer("Cosmo Ópticas");
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const logoBytes = suppliedLogo === undefined ? await loadLogo() : suppliedLogo;
  const logo = logoBytes ? await document.embedJpg(logoBytes) : null;

  for (const model of models) {
    const logoHeight = logo ? Math.min(72, 119 * logo.height / logo.width) : 22;
    const subtitleRows = model.subtitle ? wrap(regular, model.subtitle, 7.1).length : 0;
    const attendantRows = model.attendant ? wrap(regular, `Atendió: ${model.attendant}`, 7).length : 0;
    const headerHeight = 12 + logoHeight + 9 + 13 + 12 + subtitleRows * 9 + attendantRows * 9 + 7;
    const contentHeight = model.blocks.reduce((sum, block) => sum + blockHeight(block, regular, bold), 0);
    const footerRows = model.footer ? wrap(regular, model.footer, 6.5).length : 0;
    const footerHeight = footerRows ? footerRows * 8 + 16 : 12;
    const height = Math.max(360, headerHeight + contentHeight + footerHeight);
    const page = document.addPage([RECEIPT_WIDTH, height]);
    let y = height - 12;

    if (logo) {
      const imageWidth = 119;
      const imageHeight = Math.min(72, imageWidth * logo.height / logo.width);
      page.drawImage(logo, {
        x: (RECEIPT_WIDTH - imageWidth) / 2,
        y: y - imageHeight,
        width: imageWidth,
        height: imageHeight,
      });
      y -= imageHeight + 9;
    } else {
      drawCentered(page, bold, "COSMO ÓPTICAS", y - 13, 13, rgb(NAVY.r, NAVY.g, NAVY.b));
      y -= 31;
    }

    const title = safeText(model.title.toUpperCase());
    const titleSize = title.length > 32 ? 8.8 : 10;
    drawCentered(page, bold, title, y - 10, titleSize, rgb(NAVY.r, NAVY.g, NAVY.b));
    y -= 23;
    drawCentered(page, bold, model.folio, y, 9, rgb(NAVY.r, NAVY.g, NAVY.b));
    y -= 12;
    if (model.subtitle) {
      for (const row of wrap(regular, model.subtitle, 7.1)) {
        drawCentered(page, regular, row, y, 7.1, rgb(MUTED.r, MUTED.g, MUTED.b));
        y -= 9;
      }
    }
    if (model.attendant) {
      for (const row of wrap(regular, `Atendió: ${model.attendant}`, 7)) {
        drawCentered(page, regular, row, y, 7, rgb(MUTED.r, MUTED.g, MUTED.b));
        y -= 9;
      }
    }
    y -= 7;

    for (const block of model.blocks) {
      if (block.kind === "spacer") { y -= block.height; continue; }
      if (block.kind === "rule") {
        y -= 4;
        page.drawLine({
          start: { x: MARGIN + 2, y }, end: { x: RECEIPT_WIDTH - MARGIN - 2, y },
          thickness: 0.65, color: rgb(0.42, 0.65, 0.69), dashArray: [3, 3],
        });
        y -= 7;
        continue;
      }
      if (block.kind === "section") {
        y -= 5;
        page.drawRectangle({ x: MARGIN, y: y - 17, width: CONTENT_WIDTH, height: 17, color: rgb(TEAL.r, TEAL.g, TEAL.b) });
        page.drawText(safeText(block.text.toUpperCase()), {
          x: MARGIN + 6, y: y - 11.6, size: 7.6, font: bold, color: rgb(WHITE.r, WHITE.g, WHITE.b),
        });
        y -= 29;
        continue;
      }
      if (block.kind === "total") {
        page.drawRectangle({ x: MARGIN, y: y - 22, width: CONTENT_WIDTH, height: 22, color: rgb(NAVY.r, NAVY.g, NAVY.b) });
        page.drawText(safeText(block.label.toUpperCase()), {
          x: MARGIN + 6, y: y - 14.5, size: 8.4, font: bold, color: rgb(WHITE.r, WHITE.g, WHITE.b),
        });
        const value = safeText(block.value);
        page.drawText(value, {
          x: RECEIPT_WIDTH - MARGIN - 6 - bold.widthOfTextAtSize(value, 9), y: y - 15,
          size: 9, font: bold, color: rgb(WHITE.r, WHITE.g, WHITE.b),
        });
        y -= 29;
        continue;
      }
      if (block.kind === "signatures") {
        y -= 20;
        page.drawLine({ start: { x: 16, y }, end: { x: 103, y }, thickness: 0.55, color: rgb(NAVY.r, NAVY.g, NAVY.b) });
        page.drawLine({ start: { x: 124, y }, end: { x: 211, y }, thickness: 0.55, color: rgb(NAVY.r, NAVY.g, NAVY.b) });
        y -= 10;
        const leftRows = wrap(regular, block.left, 6.2, 84);
        const rightRows = wrap(regular, block.right, 6.2, 84);
        leftRows.slice(0, 2).forEach((row, index) => page.drawText(row, {
          x: 17, y: y - index * 8, size: 6.2, font: regular, color: rgb(MUTED.r, MUTED.g, MUTED.b),
        }));
        rightRows.slice(0, 2).forEach((row, index) => page.drawText(row, {
          x: 125, y: y - index * 8, size: 6.2, font: regular, color: rgb(MUTED.r, MUTED.g, MUTED.b),
        }));
        y -= 18;
        continue;
      }
      if (block.kind === "charge") {
        const label = safeText(block.label);
        const value = safeText(block.value);
        page.drawText(label, { x: MARGIN + 2, y, size: 7.8, font: bold, color: rgb(NAVY.r, NAVY.g, NAVY.b) });
        page.drawText(value, {
          x: RECEIPT_WIDTH - MARGIN - 2 - bold.widthOfTextAtSize(value, 7.8), y,
          size: 7.8, font: bold, color: rgb(NAVY.r, NAVY.g, NAVY.b),
        });
        y -= 10;
        if (block.details) {
          for (const row of wrap(regular, block.details, 6.7)) {
            page.drawText(row, { x: MARGIN + 2, y, size: 6.7, font: regular, color: rgb(MUTED.r, MUTED.g, MUTED.b) });
            y -= 8;
          }
        }
        y -= 7;
        continue;
      }
      if (block.kind === "pair") {
        const label = safeText(block.label);
        const value = safeText(block.value);
        const valueFont = block.bold ? bold : regular;
        const labelWidth = regular.widthOfTextAtSize(label, 7.4);
        const valueWidth = valueFont.widthOfTextAtSize(value, 7.4);
        if (labelWidth + valueWidth <= CONTENT_WIDTH - 3) {
          page.drawText(label, { x: MARGIN + 2, y, size: 7.4, font: regular, color: rgb(MUTED.r, MUTED.g, MUTED.b) });
          page.drawText(value, {
            x: RECEIPT_WIDTH - MARGIN - 2 - valueWidth, y, size: 7.4, font: valueFont,
            color: rgb(NAVY.r, NAVY.g, NAVY.b),
          });
          y -= 15;
        } else {
          page.drawText(label, { x: MARGIN + 2, y, size: 7.2, font: regular, color: rgb(MUTED.r, MUTED.g, MUTED.b) });
          y -= 10;
          for (const row of wrap(valueFont, value, 7.4)) {
            page.drawText(row, { x: MARGIN + 2, y, size: 7.4, font: valueFont, color: rgb(NAVY.r, NAVY.g, NAVY.b) });
            y -= 9;
          }
          y -= 3;
        }
        continue;
      }

      const size = block.size || 8;
      const font = block.bold ? bold : regular;
      for (const row of wrap(font, block.text, size)) {
        const rowWidth = font.widthOfTextAtSize(row, size);
        const x = block.align === "center" ? (RECEIPT_WIDTH - rowWidth) / 2
          : block.align === "right" ? RECEIPT_WIDTH - MARGIN - 2 - rowWidth : MARGIN + 2;
        const color = block.muted ? MUTED : NAVY;
        page.drawText(row, { x: Math.max(MARGIN, x), y, size, font, color: rgb(color.r, color.g, color.b) });
        y -= size + 3;
      }
      y -= 2;
    }

    if (model.footer) {
      y -= 4;
      for (const row of wrap(regular, model.footer, 6.5)) {
        drawCentered(page, regular, row, y, 6.5, rgb(MUTED.r, MUTED.g, MUTED.b));
        y -= 8;
      }
    }
  }

  return document.save();
}

function branchSubtitle(branchName: string, branchCode: string, date: string): string {
  return `${branchName} #${branchCode} | ${shortDate(date)}`;
}

function frameMeasurements(frame: OpticalSale["frame"]): string | null {
  const item = frame.measurements;
  if (!item) return null;
  return `A ${item.measureA} mm | B ${item.measureB} mm | DBL ${item.measureDbl} mm | ED ${item.measureEd} mm${item.baseCurve ? ` | Curva ${item.baseCurve}` : ""}`;
}

function catalogFrameDetails(frame: OpticalSale["frame"]): string {
  const values = [
    frame.sku ? `SKU ${frame.sku}` : null,
    frame.partNumber ? `Parte ${frame.partNumber}` : null,
    frame.brand ? `Marca ${frame.brand}` : null,
    frame.material ? `Material ${frame.material}` : null,
    frame.color ? `Color ${frame.color}` : null,
  ].filter(Boolean);
  return values.length ? values.join(" | ") : frame.description;
}

function customerFrameDetails(frame: OpticalSale["frame"]): string {
  const details = frame.customerDetails;
  if (!details) return frame.description;
  return `Marca ${details.brand} | Modelo ${details.model} | Material ${details.material} | Condición ${details.condition}`;
}

function signedSphere(value: number): string {
  return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
}

function eyeText(eye: OpticalSale["prescription"]["od"]): string {
  return [
    `Esf ${signedSphere(eye.sphere)}`,
    `Cil ${eye.cylinder.toFixed(2)}`,
    `Eje ${eye.axis ?? "-"}`,
    `ADD ${eye.addition?.toFixed(2) ?? "-"}`,
    `DNP ${eye.dnp}`,
    `CB ${eye.baseCurve ?? "-"}`,
    `Alt ${eye.height}`,
    `Cor ${eye.corridor ?? "-"}`,
  ].join(" | ");
}

function prismText(label: string, eye: OpticalSale["prescription"]["prismData"]["od"]): string | null {
  if (!eye?.primary) return null;
  const primary = `${eye.primary.value.toFixed(2)} ${eye.primary.direction}`;
  const secondary = eye.secondary ? ` + ${eye.secondary.value.toFixed(2)} ${eye.secondary.direction}` : "";
  return `${label} | Prisma ${primary}${secondary}`;
}

function componentTitle(componentType: string): string {
  const labels: Record<string, string> = {
    BASE: "Lentes",
    AR: "Antirreflejante (AR)",
    FOTOSENSIBLE: "Fotosensible",
    UV_COLOR: "Extras / color",
    RANGO_RX: "Extra por rango RX",
  };
  return labels[componentType] || componentType.replaceAll("_", " ");
}

function opticalPriceBlocks(sale: OpticalSale): TicketBlock[] {
  const frameBlock: TicketBlock = {
    kind: "charge",
    label: sale.frame.origin === "CUSTOMER" ? "Montaje en armazón del cliente" : "Armazón",
    details: sale.frame.origin === "CUSTOMER" ? customerFrameDetails(sale.frame) : catalogFrameDetails(sale.frame),
    value: money.format(sale.framePrice),
  };
  const breakdown = sale.lens.priceBreakdown || [];
  const lensBlocks: TicketBlock[] = breakdown.length
    ? breakdown.map((component) => ({
      kind: "charge" as const,
      label: componentTitle(component.componentType),
      details: component.label,
      value: component.salePrice === null ? "Precio no disponible" : money.format(component.salePrice),
    }))
    : [{ kind: "charge", label: "Lentes", details: sale.lens.description, value: money.format(sale.lensPrice) }];
  return [frameBlock, ...lensBlocks];
}

function specificationBlocks(sale: OpticalSale): TicketBlock[] {
  return (sale.lens.priceBreakdown || [])
    .filter((component) => component.componentType !== "BASE")
    .map((component) => ({
      kind: "pair" as const,
      label: componentTitle(component.componentType),
      value: component.label,
      bold: true,
    }));
}

function paymentBlocks(payments: OpticalSale["payments"]): TicketBlock[] {
  return payments.flatMap((payment, index) => [
    { kind: "pair" as const, label: `Pago ${index + 1} - ${payment.methodCode} ${payment.methodLabel}`, value: money.format(payment.amount), bold: true },
    ...(payment.reference ? [{ kind: "text" as const, text: `Referencia: ${payment.reference}`, size: 6.8, muted: true }] : []),
  ]);
}

function layawayPolicy(sale: Pick<OpticalSale, "paidPercentage" | "layawayExpiresAt">): string {
  const expiry = sale.layawayExpiresAt ? ` El apartado vence el ${dateOnly(sale.layawayExpiresAt)}.` : "";
  return `Anticipo recibido: ${sale.paidPercentage.toFixed(2)}%. Para iniciar elaboración y emitir la orden de laboratorio debe cubrir al menos 50% del total.${expiry}`;
}

export function opticalCustomerTicketModel(sale: OpticalSale): TicketPageModel {
  return {
    title: sale.status === "LAYAWAY" ? "COMPROBANTE DE APARTADO" : "COMPROBANTE DE VENTA",
    folio: sale.folio,
    subtitle: branchSubtitle(sale.branchName, sale.branchCode, sale.createdAt),
    attendant: sale.createdByName,
    blocks: [
      { kind: "section", text: "Cliente" },
      { kind: "text", text: sale.customer.fullName, bold: true },
      { kind: "section", text: "Productos y servicios" },
      { kind: "text", text: "Precios con IVA incluido.", size: 6.7, muted: true },
      ...opticalPriceBlocks(sale),
      { kind: "rule" },
      { kind: "pair", label: "Subtotal sin IVA", value: money.format(sale.subtotal) },
      { kind: "pair", label: "IVA incluido (16%)", value: money.format(sale.vat) },
      { kind: "total", label: "Total", value: money.format(sale.total) },
      { kind: "pair", label: "Pagado", value: money.format(sale.paid), bold: true },
      { kind: "pair", label: "Saldo", value: money.format(sale.balance), bold: true },
      { kind: "section", text: "Pagos" },
      ...paymentBlocks(sale.payments),
      ...(sale.status === "LAYAWAY" ? [
        { kind: "section" as const, text: "Condiciones del apartado" },
        { kind: "text" as const, text: layawayPolicy(sale), size: 7.2 },
      ] : []),
      { kind: "section", text: "Entrega y facturación" },
      { kind: "pair", label: "Entrega estimada", value: dateOnly(sale.estimatedDeliveryDate), bold: true },
      { kind: "pair", label: "Factura", value: sale.balance <= 0.009 ? (sale.invoiceRequested ? "Solicitada" : "No solicitada") : "Disponible al liquidar" },
    ],
    footer: "Gracias por su preferencia. Conserve este comprobante.",
  };
}

export function opticalInternalTicketModel(sale: OpticalSale): TicketPageModel {
  return {
    title: "COPIA ÓPTICA",
    folio: sale.folio,
    subtitle: branchSubtitle(sale.branchName, sale.branchCode, sale.createdAt),
    attendant: sale.createdByName,
    blocks: [
      { kind: "section", text: "Cliente" },
      { kind: "text", text: sale.customer.fullName, bold: true },
      { kind: "section", text: "Productos y servicios" },
      { kind: "text", text: "Precios con IVA incluido.", size: 6.7, muted: true },
      ...opticalPriceBlocks(sale),
      { kind: "rule" },
      { kind: "pair", label: "Bisel", value: [sale.bevelType, sale.bevelFinish].filter(Boolean).join(" - ") },
      { kind: "pair", label: "Total", value: money.format(sale.total), bold: true },
      { kind: "pair", label: "Pagado", value: money.format(sale.paid) },
      { kind: "pair", label: "Saldo", value: money.format(sale.balance), bold: true },
      { kind: "pair", label: "Entrega estimada", value: dateOnly(sale.estimatedDeliveryDate), bold: true },
      ...(sale.status === "LAYAWAY" ? [
        { kind: "section" as const, text: "Condiciones del apartado" },
        { kind: "text" as const, text: layawayPolicy(sale), size: 7.2 },
      ] : []),
    ],
    footer: "Copia interna de la óptica.",
  };
}

export function opticalLaboratoryTicketModel(sale: OpticalSale): TicketPageModel {
  const measurements = frameMeasurements(sale.frame);
  const odPrism = prismText("OD", sale.prescription.prismData.od);
  const oiPrism = prismText("OI", sale.prescription.prismData.oi);
  return {
    title: "ORDEN DE LABORATORIO",
    folio: sale.folio,
    subtitle: `${sale.branchName} #${sale.branchCode} | Entrega ${dateOnly(sale.estimatedDeliveryDate)}`,
    blocks: [
      { kind: "section", text: "Armazón" },
      { kind: "text", text: sale.frame.origin === "CUSTOMER" ? "Armazón proporcionado por el cliente" : catalogFrameDetails(sale.frame), bold: true },
      ...(sale.frame.origin === "CUSTOMER" ? [{ kind: "text" as const, text: customerFrameDetails(sale.frame) }] : []),
      ...(measurements ? [{ kind: "text" as const, text: measurements }] : []),
      { kind: "pair", label: "Bisel", value: [sale.bevelType, sale.bevelFinish].filter(Boolean).join(" - ") },
      { kind: "section", text: "Lentes" },
      { kind: "text", text: `${sale.lens.lensType} | ${sale.lens.design} | ${sale.lens.material}`, bold: true },
      ...specificationBlocks(sale),
      { kind: "section", text: "Graduación" },
      { kind: "text", text: "OJO DERECHO (OD)", bold: true, size: 8.6 },
      { kind: "text", text: eyeText(sale.prescription.od), bold: true, size: 7.6 },
      ...(odPrism ? [{ kind: "text" as const, text: odPrism, bold: true }] : []),
      { kind: "rule" },
      { kind: "spacer", height: 6 },
      { kind: "text", text: "OJO IZQUIERDO (OI)", bold: true, size: 8.6 },
      { kind: "text", text: eyeText(sale.prescription.oi), bold: true, size: 7.6 },
      ...(oiPrism ? [{ kind: "text" as const, text: oiPrism, bold: true }] : []),
      { kind: "section", text: "Comentarios para laboratorio" },
      { kind: "text", text: sale.prescription.laboratoryComments?.trim() || "Sin observaciones" },
      { kind: "rule" },
    ],
    footer: "Orden para laboratorio y banco de bisel.",
  };
}

export function customerFrameResponsivaModels(sale: OpticalSale): TicketPageModel[] {
  const details = sale.frame.customerDetails;
  if (sale.frame.origin !== "CUSTOMER" || !details) throw new Error("La venta no contiene un armazón entregado por el cliente.");
  const policy = "El cliente entrega su armazón y declara correctos los datos y observaciones visibles. Cosmo Ópticas realizará el trabajo con cuidados razonables. En armazones ajenos o usados puede existir fractura, deformación, desprendimiento de recubrimientos o daño no visible durante el ajuste, calor, biselado o montaje. La recepción no implica asumir daños originados por desgaste, reparaciones o deterioro preexistente.";
  return ["COPIA CLIENTE", "COPIA ÓPTICA"].map((copy) => ({
    title: "RESPONSIVA DE ARMAZÓN DEL CLIENTE",
    folio: sale.folio,
    subtitle: `${copy} | ${branchSubtitle(sale.branchName, sale.branchCode, sale.createdAt)}`,
    attendant: sale.createdByName,
    blocks: [
      { kind: "section", text: "Recepción del material" },
      { kind: "pair", label: "Cliente", value: sale.customer.fullName, bold: true },
      { kind: "pair", label: "Marca", value: details.brand },
      { kind: "pair", label: "Modelo", value: details.model },
      { kind: "pair", label: "Material", value: details.material },
      { kind: "pair", label: "Condición", value: details.condition },
      { kind: "pair", label: "Bisel", value: [sale.bevelType, sale.bevelFinish].filter(Boolean).join(" - ") },
      { kind: "section", text: "Observaciones visibles" },
      { kind: "text", text: details.comments || "Sin observaciones visibles registradas." },
      { kind: "section", text: "Responsiva" },
      { kind: "text", text: policy, size: 7.2 },
      { kind: "spacer", height: 8 },
      { kind: "signatures", left: "Firma del cliente", right: "Nombre y firma de quien recibe" },
    ],
    footer: "Esta responsiva no incluye graduación ni medidas técnicas de laboratorio.",
  }));
}

export function counterSaleTicketModel(sale: CounterSale): TicketPageModel {
  const itemBlocks: TicketBlock[] = sale.items.map((item) => ({
    kind: "charge",
    label: `${item.quantity} x ${item.description}`,
    details: `SKU ${item.sku} | Marca ${item.brand} | Color ${item.color} | ${money.format(item.unitPrice)} c/u`,
    value: money.format(item.lineTotal),
  }));
  const payments: TicketBlock[] = sale.payments.flatMap((payment, index) => [
    { kind: "pair" as const, label: `Pago ${index + 1} - ${payment.methodCode} ${payment.methodLabel}`, value: money.format(payment.amount), bold: true },
    ...(payment.reference ? [{ kind: "text" as const, text: `Referencia: ${payment.reference}`, size: 6.8, muted: true }] : []),
  ]);
  const paidPercentage = sale.total > 0 ? sale.paid / sale.total * 100 : 0;
  return {
    title: sale.operationType === "LAYAWAY_50" ? "COMPROBANTE DE APARTADO" : "COMPROBANTE DE VENTA",
    folio: sale.folio,
    subtitle: branchSubtitle(sale.branchName, sale.branchCode, sale.createdAt),
    attendant: sale.createdByName,
    blocks: [
      ...(sale.operationType === "LAYAWAY_50" ? [{ kind: "text" as const, text: sale.customerName, bold: true, align: "center" as const }] : []),
      { kind: "section", text: "Productos" },
      { kind: "text", text: "Precios con IVA incluido.", size: 6.7, muted: true },
      ...itemBlocks,
      { kind: "rule" },
      { kind: "pair", label: "Subtotal sin IVA", value: money.format(sale.subtotal) },
      { kind: "pair", label: "IVA incluido (16%)", value: money.format(sale.vat) },
      { kind: "total", label: "Total", value: money.format(sale.total) },
      { kind: "pair", label: "Pagado", value: money.format(sale.paid), bold: true },
      { kind: "pair", label: "Saldo", value: money.format(sale.balance), bold: true },
      { kind: "section", text: "Pagos" },
      ...payments,
      ...(sale.layawayExpiresAt ? [
        { kind: "section" as const, text: "Condiciones del apartado" },
        { kind: "text" as const, text: `Anticipo recibido: ${paidPercentage.toFixed(2)}%. El apartado vence el ${dateOnly(sale.layawayExpiresAt)}.`, size: 7.2 },
      ] : []),
    ],
    footer: "Gracias por su preferencia. Conserve este comprobante.",
  };
}

export function workPaymentTicketModel(
  detail: WorkDetail,
  selectedPayment: WorkPaymentHistoryItem | null = detail.lastPayment,
): TicketPageModel {
  if (!selectedPayment) throw new Error("No hay un abono registrado para generar el comprobante.");
  const payment = selectedPayment;
  return {
    title: payment.balanceAfter <= 0.009 ? "COMPROBANTE DE LIQUIDACIÓN" : "COMPROBANTE DE ABONO",
    folio: detail.saleFolio,
    subtitle: branchSubtitle(detail.branchName, detail.branchCode, payment.receivedAt),
    attendant: detail.createdByName,
    blocks: [
      { kind: "section", text: "Cliente" },
      { kind: "text", text: detail.customerName, bold: true },
      { kind: "section", text: "Pago recibido" },
      { kind: "pair", label: `${payment.methodCode} ${payment.methodLabel}`, value: money.format(payment.amount), bold: true },
      ...(payment.reference ? [{ kind: "text" as const, text: `Referencia: ${payment.reference}`, size: 6.8, muted: true }] : []),
      { kind: "rule" },
      { kind: "pair", label: "Total de la venta", value: money.format(detail.total) },
      { kind: "pair", label: "Pagado acumulado", value: money.format(payment.accumulatedPaid), bold: true },
      { kind: "total", label: "Saldo", value: money.format(payment.balanceAfter) },
    ],
    footer: "Conserve este comprobante.",
  };
}

export function cancellationTicketModel(request: CancellationRequest, creditNote = false, redemptionCode?: string): TicketPageModel {
  return {
    title: creditNote ? "NOTA DE CRÉDITO" : "COMPROBANTE DE CANCELACIÓN",
    folio: creditNote ? request.creditNote?.folio || request.folio : request.folio,
    subtitle: `${request.branchName} #${request.branchCode}`,
    blocks: [
      { kind: "section", text: "Operación" },
      { kind: "pair", label: "Venta original", value: request.saleFolio, bold: true },
      { kind: "pair", label: "Cliente", value: request.customerName },
      { kind: "pair", label: "Tipo", value: request.operationalType.replaceAll("_", " ") },
      { kind: "pair", label: "Destino", value: request.financialDestination.replaceAll("_", " ") },
      { kind: "text", text: request.reason },
      { kind: "rule" },
      { kind: "total", label: creditNote ? "Importe autorizado" : "Importe cancelado", value: money.format(request.authorizedAmount) },
      ...(request.creditNote ? [{ kind: "pair" as const, label: "Saldo de nota", value: money.format(request.creditNote.balance), bold: true }] : []),
      ...(creditNote && redemptionCode ? [
        { kind: "section" as const, text: "Código único de aplicación" },
        { kind: "text" as const, text: redemptionCode, bold: true, align: "center" as const, size: 12 },
        { kind: "text" as const, text: "Conserve este código. Sólo puede aplicarse de forma auditada contra su saldo vigente.", align: "center" as const, size: 7 },
      ] : []),
      { kind: "pair", label: "Estado", value: request.status, bold: true },
      { kind: "pair", label: "Ejecutó", value: request.executedBy || "Pendiente" },
      { kind: "signatures", left: "Firma del cliente", right: "Firma de autorización" },
    ],
    footer: "Documento auditable. Conserve este comprobante.",
  };
}

export async function buildOpticalCustomerTicketPdf(sale: OpticalSale): Promise<Uint8Array> {
  return renderTicketPages([opticalCustomerTicketModel(sale)]);
}

export async function buildOpticalInternalTicketPdf(sale: OpticalSale): Promise<Uint8Array> {
  return renderTicketPages([opticalInternalTicketModel(sale)]);
}

export async function buildOpticalLaboratoryTicketPdf(sale: OpticalSale): Promise<Uint8Array> {
  return renderTicketPages([opticalLaboratoryTicketModel(sale)]);
}

export async function buildCustomerFrameResponsivaPdf(sale: OpticalSale): Promise<Uint8Array> {
  return renderTicketPages(customerFrameResponsivaModels(sale));
}

export async function buildOpticalTicketPackagePdf(sale: OpticalSale, suppliedLogo?: Uint8Array | null): Promise<Uint8Array> {
  return renderTicketPages([
    opticalCustomerTicketModel(sale),
    opticalInternalTicketModel(sale),
    ...(sale.frame.origin === "CUSTOMER" && sale.frame.customerDetails ? customerFrameResponsivaModels(sale) : []),
    ...(sale.laboratoryAuthorized ? [opticalLaboratoryTicketModel(sale)] : []),
  ], suppliedLogo);
}

export async function buildCounterSaleTicketPdf(sale: CounterSale): Promise<Uint8Array> {
  return renderTicketPages([counterSaleTicketModel(sale)]);
}

export async function buildWorkPaymentTicketPdf(
  detail: WorkDetail,
  payment: WorkPaymentHistoryItem | null = detail.lastPayment,
): Promise<Uint8Array> {
  return renderTicketPages([workPaymentTicketModel(detail, payment)]);
}

export async function buildCancellationTicketPdf(request: CancellationRequest, creditNote = false, redemptionCode?: string): Promise<Uint8Array> {
  return renderTicketPages([cancellationTicketModel(request, creditNote, redemptionCode)]);
}

export const ticketPdfTestSupport = { renderTicketPages };
